<template>
  <div class="min-h-screen">
    <AppNavbar :showLogout="true" />

    <main class="max-w-7xl mx-auto px-8 py-10">

      <!-- ── Hero banner ── -->
      <div class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 px-8 pt-10 pb-0 mb-8 text-white">
        <!-- Decorative circles -->
        <div class="pointer-events-none absolute -top-12 -right-12 w-72 h-72 rounded-full bg-white/5"></div>
        <div class="pointer-events-none absolute top-6 right-32 w-40 h-40 rounded-full bg-white/5"></div>
        <div class="pointer-events-none absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-blue-500/20"></div>

        <!-- Top row: greeting + cab icon -->
        <div class="relative flex items-start justify-between mb-8">
          <div>
            <p class="text-blue-200 text-sm font-medium mb-1">{{ greeting }}</p>
            <h1 class="text-3xl font-extrabold tracking-tight mb-2">
              Welcome back, {{ user?.firstName }}
            </h1>
            <p class="text-blue-200 text-sm max-w-md leading-relaxed">
              Here's a snapshot of your cab bookings and activity. Use the cards below to get started.
            </p>
          </div>
          <!-- Decorative icon block -->
          <div class="hidden lg:flex w-20 h-20 rounded-2xl bg-white/10 items-center justify-center flex-shrink-0 ml-8 mt-1">
            <svg class="w-10 h-10 text-white opacity-80" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.04 3H5.81l1.04-3zM19 17H5v-5h14v5z"/>
              <circle cx="7.5" cy="14.5" r="1.5"/>
              <circle cx="16.5" cy="14.5" r="1.5"/>
            </svg>
          </div>
        </div>

        <!-- Stats strip sitting on the bottom edge of the hero -->
        <div class="relative grid grid-cols-3 gap-4 -mb-px">
          <!-- Bookings stat -->
          <div class="bg-white/10 backdrop-blur-sm rounded-t-xl px-6 py-5 border border-white/10 border-b-0">
            <div v-if="statsLoading" class="h-8 w-12 bg-white/20 rounded animate-pulse mb-1"></div>
            <p v-else class="text-2xl font-extrabold leading-none mb-1">
              {{ stats.bookings !== null ? stats.bookings : '—' }}
            </p>
            <p class="text-blue-200 text-xs font-medium uppercase tracking-wide">Total bookings</p>
          </div>
          <!-- Locations stat -->
          <div class="bg-white/10 backdrop-blur-sm rounded-t-xl px-6 py-5 border border-white/10 border-b-0">
            <div v-if="statsLoading" class="h-8 w-12 bg-white/20 rounded animate-pulse mb-1"></div>
            <p v-else class="text-2xl font-extrabold leading-none mb-1">
              {{ stats.locations !== null ? stats.locations : '—' }}
            </p>
            <p class="text-blue-200 text-xs font-medium uppercase tracking-wide">Saved locations</p>
          </div>
          <!-- Notifications stat -->
          <div class="bg-white/10 backdrop-blur-sm rounded-t-xl px-6 py-5 border border-white/10 border-b-0">
            <div v-if="statsLoading" class="h-8 w-12 bg-white/20 rounded animate-pulse mb-1"></div>
            <p v-else class="text-2xl font-extrabold leading-none mb-1">
              {{ stats.notifications !== null ? stats.notifications : '—' }}
            </p>
            <p class="text-blue-200 text-xs font-medium uppercase tracking-wide">Inbox items</p>
          </div>
        </div>
      </div>
      <!-- ── /Hero banner ── -->

      <!-- Section label -->
      <p class="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">Quick access</p>

      <!-- Feature cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <DashboardCard
          title="Bookings"
          description="Create new rides, track your booking history, and manage all your trips in one place."
          to="/bookings"
          icon="car"
          color="blue"
        />
        <DashboardCard
          title="Payments"
          description="Pay for outstanding bookings by card and review your full payment history."
          to="/payments"
          icon="card"
          color="sky"
        />
        <DashboardCard
          title="Locations"
          description="Save your favourite pickup spots, manage addresses, and check live weather at any location."
          to="/locations"
          icon="pin"
          color="emerald"
        />
        <DashboardCard
          title="Inbox"
          description="View cab-ready notifications, discount alerts, and updates from the platform."
          to="/notifications"
          icon="bell"
          color="violet"
        />
      </div>

    </main>
  </div>
</template>

<script>
import AppNavbar from "@/components/AppNavbar.vue";
import DashboardCard from "@/components/DashboardCard.vue";

export default {
  name: "DashboardView",
  components: { AppNavbar, DashboardCard },
  data() {
    return {
      statsLoading: false,
      stats: { bookings: null, locations: null, notifications: null },
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 12) return 'Good morning';
      if (h < 18) return 'Good afternoon';
      return 'Good evening';
    },
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push("/login");
      return;
    }
    this.loadStats();
  },
  methods: {
    async loadStats() {
      this.statsLoading = true;
      try {
        const [bRes, lRes, nRes] = await Promise.all([
          this.$store.dispatch("fetchBookings"),
          this.$store.dispatch("fetchLocations"),
          this.$store.dispatch("fetchNotifications"),
        ]);
        this.stats.bookings      = bRes.data.bookings?.length      ?? 0;
        this.stats.locations     = lRes.data.locations?.length     ?? 0;
        this.stats.notifications = nRes.data.notifications?.length ?? 0;
      } catch (_) {
        // stats are display-only, silently fail
      } finally {
        this.statsLoading = false;
      }
    },
  },
};
</script>
